import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Vector3, Vector2 } from "@babylonjs/core/Maths/math.vector";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { GridMaterial } from "@babylonjs/materials/grid/gridMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Layer } from "@babylonjs/core/Layers/layer";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { ExecuteCodeAction } from "@babylonjs/core/Actions/directActions";
import { LoadAssetContainerAsync, ImportMeshAsync } from "@babylonjs/core/Loading/sceneLoader";
import { EasingFunction } from "@babylonjs/core/Animations/easing";
import { CubicEase } from "@babylonjs/core/Animations/easing";
import { Axis, Space } from "@babylonjs/core/Maths/math.axis";
import { Animation } from "@babylonjs/core/Animations/animation";
import { CreateScreenshotAsync } from "@babylonjs/core/Misc/screenshotTools";
import { GLTF2Export } from "@babylonjs/serializers/glTF";
import { Material } from "@babylonjs/core/Materials/material";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Sprite } from "@babylonjs/core/Sprites/sprite";
import { SpriteManager } from "@babylonjs/core/Sprites/spriteManager";
import { applyMeshIdData } from "./BabylonMeshDataHelper";
import { updateColours } from "./BabylonModelPaintHelper";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Logger } from "@babylonjs/core/Misc/logger";
import "@babylonjs/loaders/OBJ";

import { API_BASE } from "../../../AppConfig";
import { BaseURL } from "../../../components/Common";
import { updateGLB } from "../../../redux_store/jobState";

export default class SceneManager {
    constructor() {
        this.engine = null;
        this.scene = null;
        this.camera = null;

        this.modelRoot = null;
        this.container = null;
        this.meshes = [];
        this.clonedMeshMaterials = [];

        this.togglePop = null;
        this.dispatch = null;

        this.metadata = {
            selectedMesh: null,
            highlighter: null,
            shadowGenerator: null,
            directionalLight: null,
            modelLights: null,
            modelFans: [],
            fanSpinObservers: [],
        };

        this.spriteData = {
            logoYOffset: 0,
            logoSprite: null,
            logoManager: null,
        };

        this.skyboxes = [];
        this.groundMeshes = [];

        this.showEnvironment = false;
        this._exportToken = 0;
    }

    init(canvas, togglePop, showEnvironment) {
        this.togglePop = togglePop;
        this.showEnvironment = showEnvironment;
        this._createEngineAndScene(canvas);
        this._initScene();
        this._startRenderLoop();
    }

    _createEngineAndScene(canvas) {
        this.engine = new Engine(canvas);
        this.scene = new Scene(this.engine);
    }

    _startRenderLoop() {
        this.engine.runRenderLoop(() => {
            if (this.scene) this.scene.render();
        });
    }

    _stopRenderLoop() {
        this.engine.stopRenderLoop();
    }

    async _initScene() {
        const scene = this.scene;
        const showEnvironment = this.showEnvironment;
        const canvas = scene.getEngine().getRenderingCanvas();

        const camera = new ArcRotateCamera("cam", Math.PI / 3, Math.PI / 2.5, 1500, Vector3.Zero(), scene);
        camera.setTarget(Vector3.Zero());
        camera.attachControl(canvas, true);
        camera.targetScreenOffset = new Vector2(-80, 0);
        camera.wheelPrecision = 0.75;
        camera.minZ = 5;
        camera.maxZ = 20000;
        camera.upperRadiusLimit = 5000;
        camera.lowerRadiusLimit = 10;
        camera.panningSensibility = 10;
        this.camera = camera;

        const dirLight = new DirectionalLight("dir", new Vector3(1, -1, -0.5), scene);
        const hemLight = new HemisphericLight("hemi", new Vector3(1, 1, 0), scene);
        hemLight.intensity = 0.5;
        this.metadata.directionalLight = dirLight;

        const { gridMat, gridGroundMat, grassMat, floorMat } = this._createGroundMaterials(scene);
        const ground = CreateDisc("gridGround", { radius: 5000, arc: 1, tessellation: 24 }, scene);
        ground.rotation = new Vector3(Math.PI / 2, 0, 0);
        ground.receiveShadows = true;
        ground.material = gridMat;
        ground.isPickable = false;
        ground.isVisible = !showEnvironment;

        const ground2 = CreateDisc("solidGround", { radius: 5000, arc: 1, tessellation: 24 }, scene);
        ground2.rotation = new Vector3(Math.PI / 2, 0, 0);
        ground2.receiveShadows = true;
        ground2.material = gridGroundMat;
        ground2.isPickable = false;
        ground2.isVisible = !showEnvironment;

        const ground3 = CreateDisc("grassGround", { radius: 5000, arc: 1, tessellation: 24 }, scene);
        ground3.rotation = new Vector3(Math.PI / 2, 0, 0);
        ground3.receiveShadows = true;
        ground3.material = grassMat;
        ground3.isPickable = false;
        ground3.isVisible = showEnvironment;

        const floor = CreateGround("modelFloor", { width: 1, height: 1 }, scene);
        floor.material = floorMat;
        floor.position.y += 1;

        this.groundMeshes = [ground, ground2, ground3, floor];

        new Layer("background", BaseURL("models/textures/bg.jpg"), scene, true);

        this._addSky(showEnvironment);
        this._addHighlighter();
    }

    _createGroundMaterials(scene) {
        const gridMat = new GridMaterial("gridMat", scene);
        Object.assign(gridMat, {
            majorUnitFrequency: 10,
            minorUnitVisibility: 0.2,
            gridRatio: 10,
            opacity: 0.8,
            mainColor: new Color3(0, 0.05, 0.1),
            lineColor: Color3.Teal(),
        });

        const gridGroundMat = new StandardMaterial("gridGroundMat", scene);
        Object.assign(gridGroundMat, {
            alpha: 0.4,
            specularColor: new Color3(0.1, 0.1, 0.1),
            ambientColor: Color3.FromHexString("#444444"),
            diffuseColor: Color3.FromHexString("#444444"),
        });

        const grassMat = new StandardMaterial("grassMat", scene);
        const tex = new Texture(BaseURL("images/grass.jpg"), scene);
        tex.uScale = 15.0;
        tex.vScale = 15.0;
        grassMat.diffuseTexture = tex;
        grassMat.specularColor = new Color3(0.1, 0.1, 0.1);
        grassMat.backFaceCulling = true;

        const floorMat = new StandardMaterial("floorMat", scene);
        floorMat.backFaceCulling = true;

        return { gridMat, gridGroundMat, grassMat, floorMat };
    }

    _addSky(showSky) {
        const scene = this.scene;
        const SKYBOX_MODE = 5;

        const skybox = CreateBox("skyBox", { size: 18000.0 }, scene);
        const skyboxMaterial = new StandardMaterial("skyBox", scene);
        skyboxMaterial.backFaceCulling = false;
        skyboxMaterial.reflectionTexture = new CubeTexture(BaseURL("textures/test/TropicalSunnyDay"), scene);
        skyboxMaterial.reflectionTexture.coordinatesMode = SKYBOX_MODE;
        skyboxMaterial.diffuseColor = new Color3(0, 0, 0);
        skyboxMaterial.specularColor = new Color3(0, 0, 0);
        skybox.material = skyboxMaterial;

        const skybox2 = CreateBox("skyBox2", { size: 10000.0 }, scene);
        const skyboxMaterial2 = new StandardMaterial("skyBox2", scene);
        skyboxMaterial2.backFaceCulling = false;
        skyboxMaterial2.reflectionTexture = new CubeTexture(BaseURL("textures/test/TropicalSunnyDay"), scene);
        skyboxMaterial2.reflectionTexture.coordinatesMode = SKYBOX_MODE;
        skyboxMaterial2.diffuseColor = new Color3(0, 0, 0);
        skyboxMaterial2.specularColor = new Color3(0, 0, 0);
        skyboxMaterial2.alpha = 0.5;
        skybox2.rotation.y += Math.PI;
        skybox2.material = skyboxMaterial2;

        scene.registerBeforeRender(function () {
            skybox2.rotation.y += 0.0005;
        });

        skybox.isVisible = showSky;
        skybox2.isVisible = showSky;

        this.skyboxes = [skybox, skybox2];
    }

    _addHighlighter() {
        const scene = this.scene;
        const highlighter = new HighlightLayer("hl", scene);
        highlighter.innerGlow = true;
        this.metadata.highlighter = highlighter;

        const ignore = new Set(["skyBox", "skyBox2", "ground", "grass", "paving"]);

        scene.onPointerObservable.add((pointerInfo) => {
            if (
                (pointerInfo.type === PointerEventTypes.POINTERPICK || pointerInfo.type === 32) &&
                (!pointerInfo.pickInfo?.hit || ignore.has(pointerInfo.pickInfo?.pickedMesh?.id))
            ) {
                const mesh = this.metadata.selectedMesh;
                if (mesh) {
                    highlighter.removeMesh(mesh);

                    const md = mesh.metadata ?? {};
                    if (md.last_edgeRendererEnabled) mesh.enableEdgesRendering(0.999);
                    else mesh.disableEdgesRendering();
                    if (md.last_edgesColor) mesh.edgesColor = md.last_edgesColor;
                    if (md.last_edgesWidth) mesh.edgesWidth = md.last_edgesWidth;

                    mesh.isPickable = true;
                }
                this.metadata.selectedMesh = null;
            }
        });
    }

    attachDispatch(dispatch) {
        this.dispatch = dispatch;
    }

    async importModel(url) {
        this._assertScene();

        if (this.modelRoot) {
            this.modelRoot.setEnabled(false);
        }
        this._stopRenderLoop();

        this.disposeModel();

        let api_base = API_BASE(false, process?.env?.REACT_APP_LOCAL_CAD === "true");
        const fullUrl = api_base + "obj/" + url;

        const container = await LoadAssetContainerAsync(fullUrl, this.scene);
        container.addAllToScene();

        this.container = container;
        this.meshes = container.meshes;

        const modelRoot = this.container.createRootMesh();
        modelRoot.setEnabled(false);
        modelRoot.name = "modelRoot";
        this.modelRoot = modelRoot;

        await this._pipelineSetup();

        this._startRenderLoop();
        modelRoot.setEnabled(true);
    }

    _assertScene() {
        if (!this.scene) throw new Error("Scene not initialised");
    }

    async _pipelineSetup() {
        this._orientModel();
        this._prepShadows();
        this._prepMeshes();
        this._fitLightingToModel();
        this.paintModel();
        this._makeOverOut();
        await this.scene.whenReadyAsync();
    }

    _orientModel() {
        const modelRoot = this.modelRoot;
        if (!modelRoot) return;

        const deckMode = window?.job?.DesignParameters?.DeckMode;

        modelRoot.rotate(Axis.X, -Math.PI / 2, Space.WORLD);
        modelRoot.scaling = new Vector3(0.1, 0.1, 0.1);
        modelRoot.computeWorldMatrix(true);

        const { min, max } = modelRoot.getHierarchyBoundingVectors();
        const centre = min.add(max).scale(0.5);

        this._fitFloorToModel(min, max);

        modelRoot.position = modelRoot.position.subtract(new Vector3(centre.x, 0, centre.z));
        modelRoot.position.y += 1;

        this.spriteData.logoYOffset = deckMode ? max.y + 70 : centre.y - 0.1 * centre.y;
        this.centreModel();
    }

    _fitFloorToModel(min, max) {
        const factor = 1.2;
        const width = Math.abs(max.x - min.x) * factor;
        const depth = Math.abs(max.z - min.z) * factor;

        const floor = this.groundMeshes.find((g) => g.name === "modelFloor");

        if (floor) {
            floor.scaling = new Vector3(width, 1, depth);
            floor.isVisible = this.showEnvironment;
            floor.receiveShadows = true;
        }
    }

    _prepShadows() {
        if (this.metadata.shadowGenerator) return;

        const lightSource = this.metadata.directionalLight;
        if (!lightSource) return;

        const shadowGen = new ShadowGenerator(1024, lightSource);
        shadowGen.bias = 0.002;
        shadowGen.normalBias = 0.05;
        shadowGen.usePercentageCloserFiltering = true;

        this.metadata.shadowGenerator = shadowGen;
    }

    _prepMeshes() {
        const container = this.container;
        if (!container) return;

        const shadowGen = this.metadata.shadowGenerator;

        for (const mesh of container.meshes) {
            if (mesh === this.modelRoot) continue;

            const isFloor = mesh.id.includes("group_6");

            if (isFloor) {
                mesh.isVisible = false;
                continue;
            }

            if (mesh.material && mesh.material.clone) {
                mesh.material = mesh.material.clone(`${mesh.id}-clone`);
                mesh.material.backFaceCulling = false;

                this.clonedMeshMaterials.push(mesh.material);
            }

            if (shadowGen) shadowGen.addShadowCaster(mesh, true);
            mesh.receiveShadows = true;

            applyMeshIdData(mesh);
        }

        this._prepModelLights();
        this._prepModelFans();
    }

    _prepModelLights() {
        const glowLayer = new GlowLayer("glow", this.scene, {
            mainTextureFixedSize: 1024,
            blurKernelSize: 64,
        });
        glowLayer.intensity = 1.0;

        glowLayer.customEmissiveColorSelector = (mesh, subMesh, material, result) => {
            if (mesh?.data?.is_downlight) {
                const cw = mesh.data.dl_type?.includes("CW");
                if (cw) result.set(0.9, 0.9, 1.0, 1.0);
                else result.set(1.0, 0.8, 0.1, 1.0);
            } else {
                result.set(0, 0, 0, 0);
            }
        };

        this.metadata.modelLights = glowLayer;
    }

    async _prepModelFans() {
        const scene = this.scene;
        const shadowGen = this.metadata.shadowGenerator;
        let anchors = scene.getMeshesByTags("fan");

        for (let anchor of anchors) {
            if (shadowGen) shadowGen.removeShadowCaster(anchor);
            let fan3DObject = await ImportMeshAsync("models/Simple_Ceiling_Fan.obj", scene);

            const fanParent = new TransformNode(`fanParent_${anchor.id}`, scene);
            for (let mesh of fan3DObject.meshes) {
                mesh.setParent(fanParent);
                shadowGen.addShadowCaster(mesh);
            }

            anchor.computeWorldMatrix(true);
            anchor.refreshBoundingInfo();
            const centre = anchor.getBoundingInfo().boundingSphere.centerWorld;

            fanParent.setAbsolutePosition(centre);
            fanParent.position.y -= 270;

            let variability = Math.random() * 0.03;
            const spinObserver = scene.onBeforeRenderObservable.add(() => {
                fanParent.rotation.y -= 0.1 + variability;
            });

            this.metadata.modelFans.push(fanParent);
            this.metadata.fanSpinObservers.push(spinObserver);
        }
    }

    _fitLightingToModel() {
        const lightSource = this.metadata.directionalLight;
        const modelRoot = this.modelRoot;
        if (!lightSource || !modelRoot) return;

        const { min, max } = modelRoot.getHierarchyBoundingVectors();
        const center = min.add(max).scale(0.5);
        const size = max.subtract(min);

        const dirNorm = lightSource.direction.clone().normalize();
        const distance = Math.max(size.x, size.y, size.z) * 2.0 + 40;
        lightSource.position = center.subtract(dirNorm.scale(distance));
    }

    addLogo(logoUrl) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => {
                const imgWidth = img.width;
                const imgHeight = img.height;
                const widthRatio = 200 / imgWidth;
                const heightRatio = 200 / imgHeight;
                const scale = Math.min(widthRatio, heightRatio);

                const spriteManager = new SpriteManager(
                    "logoManager",
                    logoUrl,
                    1,
                    { width: imgWidth, height: imgHeight },
                    this.scene,
                    0
                );

                const logoSprite = new Sprite("logo", spriteManager);

                logoSprite.width = imgWidth * scale;
                logoSprite.height = imgHeight * scale;
                logoSprite.position = new Vector3(0, this.spriteData.logoYOffset, 0);

                this.spriteData.logoManager = spriteManager;
                this.spriteData.logoSprite = logoSprite;

                resolve({ spriteManager, logoSprite });
            };
            img.onerror = reject;
            img.src = logoUrl;
        });
    }

    _makeOverOut() {
        const metadata = this.metadata;
        const meshes = this.meshes;
        const camera = this.camera;
        const togglePop = this.togglePop;

        meshes.forEach((mesh) => {
            mesh.actionManager = new ActionManager(this.scene);

            mesh.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, function () {
                    if (metadata.selectedMesh === mesh) return;

                    if (mesh.material.alpha < 0.9) {
                        if (mesh.metadata) {
                            if (mesh.metadata.last_edgeRendererEnabled) {
                                mesh.enableEdgesRendering(0.999);
                            } else {
                                mesh.disableEdgesRendering();
                            }

                            if (mesh.metadata.last_edgesColor) mesh.edgesColor = mesh.metadata.last_edgesColor;
                            if (mesh.metadata.last_edgesWidth) mesh.edgesWidth = mesh.metadata.last_edgesWidth;
                        } else {
                            mesh.disableEdgesRendering();
                        }
                    }

                    if (metadata.highlighter) metadata.highlighter.removeMesh(mesh, Color3.White());
                })
            );

            mesh.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, function () {
                    if (metadata.selectedMesh === mesh) return;

                    if (metadata.highlighter) {
                        if (mesh.material.alpha < 0.9) {
                            if (!mesh.metadata) {
                                mesh.metadata = {};
                                mesh.metadata.last_edgeRendererEnabled = mesh.edgesRenderer ? true : false;
                                mesh.metadata.last_edgesColor = mesh.edgesColor;
                                mesh.metadata.last_edgesWidth = mesh.edgesWidth;
                            }

                            mesh.enableEdgesRendering(0.999);
                            mesh.edgesColor = new Color4(1, 1, 1, 1);
                            mesh.edgesWidth = 600;
                        }

                        metadata.highlighter.addMesh(mesh, Color3.White());
                    }
                })
            );

            mesh.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPickTrigger, function (e) {
                    if (typeof togglePop === "function" && mesh.data) {
                        togglePop(true, e.sourceEvent.clientX, e.sourceEvent.clientY, mesh.data);
                    }

                    const selectedMesh = metadata.selectedMesh;
                    if (selectedMesh && selectedMesh !== mesh) {
                        if (metadata.highlighter) {
                            metadata.highlighter.removeMesh(selectedMesh);
                        }

                        if (selectedMesh.material.alpha < 0.9) {
                            if (selectedMesh.metadata) {
                                if (selectedMesh.metadata.last_edgeRendererEnabled) {
                                    selectedMesh.enableEdgesRendering(0.999);
                                } else {
                                    selectedMesh.disableEdgesRendering();
                                }

                                if (selectedMesh.metadata.last_edgesColor) {
                                    selectedMesh.edgesColor = selectedMesh.metadata.last_edgesColor;
                                }

                                if (selectedMesh.metadata.last_edgesWidth) {
                                    selectedMesh.edgesWidth = selectedMesh.metadata.last_edgesWidth;
                                }
                            } else {
                                selectedMesh.disableEdgesRendering();
                            }
                        }

                        selectedMesh.isPickable = true;
                    }

                    if (mesh.material.alpha < 0.9) {
                        if (!mesh.metadata) {
                            mesh.metadata = {};
                            mesh.metadata.last_edgeRendererEnabled = mesh.edgesRenderer ? true : false;
                            mesh.metadata.last_edgesColor = mesh.edgesColor;
                            mesh.metadata.last_edgesWidth = mesh.edgesWidth;
                        }

                        mesh.enableEdgesRendering(0.999);
                        mesh.edgesColor = new Color4(0, 1, 0, 1);
                        mesh.edgesWidth = 400;
                    }

                    if (metadata.highlighter) metadata.highlighter.addMesh(mesh, Color3.Green());

                    const ease = new CubicEase();
                    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

                    Animation.CreateAndStartAnimation(
                        "at6",
                        camera,
                        "radius",
                        90,
                        75,
                        camera.radius,
                        camera.radius * (camera.radius < 1500 ? 0.99 : 0.97),
                        2,
                        ease
                    );

                    metadata.selectedMesh = mesh;
                })
            );
        });
    }

    toggleEnvironment(showEnvironment) {
        this.showEnvironment = showEnvironment;
        if (this.meshes.length === 0) return;

        for (const box of this.skyboxes) {
            box.isVisible = showEnvironment;
        }

        for (const ground of this.groundMeshes) {
            if (ground.name === "grassGround" || ground.name === "modelFloor") {
                ground.isVisible = showEnvironment;
            } else {
                ground.isVisible = !showEnvironment;
            }
        }
    }

    async paintModel() {
        updateColours(this);
        await this._exportGlb();
    }

    centreModel(reorientate = false) {
        const camera = this.camera;

        const { min, max } = this.modelRoot.getHierarchyBoundingVectors();
        const center = min.add(max).scale(0.5);
        const size = max.subtract(min);
        const maxDimension = Math.max(size.x, size.y, size.z);

        camera.setTarget(center);
        camera.radius = maxDimension * 1.4;

        if (reorientate) {
            camera.alpha = Math.PI / 3;
            camera.beta = Math.PI / 3;
        }
    }

    zoomIn() {
        const camera = this.camera;
        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        Animation.CreateAndStartAnimation(
            "zoomin",
            camera,
            "radius",
            90,
            15,
            camera.radius,
            camera.radius / 1.2,
            2,
            ease
        );
    }

    zoomOut() {
        const camera = this.camera;
        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        Animation.CreateAndStartAnimation(
            "zoomout",
            camera,
            "radius",
            90,
            15,
            camera.radius,
            camera.radius * 1.2,
            2,
            ease
        );
    }

    async takeScreenshot() {
        const engine = this.engine;
        const camera = this.camera;

        const dataUrl = await CreateScreenshotAsync(engine, camera, { width: 1920, height: 1080 });

        return dataUrl;
    }

    async _exportGlb() {
        const myToken = ++this._exportToken;
        const excludeRegex = /(group_1|group_6|group_7|grass|paving|ground|grid|background|camera|light|sky|floor)/i;
        const options = {
            shouldExportNode(node) {
                const id = node?.id ?? node?.name ?? "";
                return !excludeRegex.test(id);
            },
            animationSampleRate: 1,
        };

        const prevLog = Logger.LogLevels;
        const matsToRestore = new Map();

        try {
            Logger.LogLevels = Logger.ErrorLogLevel;

            for (const mesh of this.scene.meshes ?? []) {
                const mat = mesh?.material;

                if (mat && mat.fillMode !== Material.TriangleFillMode && !matsToRestore.has(mat)) {
                    matsToRestore.set(mat, mat.fillMode);
                    mat.fillMode = Material.TriangleFillMode;
                }
            }

            const glb = await GLTF2Export.GLBAsync(this.scene, "model", options);

            if (myToken === this._exportToken && typeof this.dispatch === "function") {
                this.dispatch(updateGLB(glb));
            }
        } catch (err) {
        } finally {
            for (const [mat, original] of matsToRestore) {
                mat.fillMode = original;
            }

            Logger.LogLevels = prevLog;
        }
    }

    disposeModel() {
        const container = this.container;
        if (!container) return;

        const { highlighter, modelLights, shadowGenerator, modelFans, fanSpinObservers } = this.metadata;
        const { logoManager, logoSprite } = this.spriteData;

        if (highlighter) {
            highlighter.removeAllMeshes();
        }

        if (modelLights) {
            try {
                modelLights.dispose();
            } catch {}
            this.metadata.modelLights = null;
        }

        if (logoSprite) {
            try {
                logoSprite.dispose();
            } catch {}
            this.spriteData.logoSprite = null;
        }

        if (logoManager) {
            try {
                logoManager.dispose();
            } catch {}
            this.spriteData.logoManager = null;
        }

        if (shadowGenerator) {
            try {
                shadowGenerator.dispose();
            } catch {}
            this.metadata.shadowGenerator = null;
        }

        if (modelFans?.length) {
            for (const fanObj of modelFans) {
                const meshes = fanObj.getChildMeshes();
                if (meshes?.length) {
                    for (const mesh of meshes) {
                        try {
                            mesh.dispose(true, true);
                        } catch {}
                    }
                }
                try {
                    fanObj.dispose(true, true);
                } catch {}
            }
            this.metadata.modelFans = [];
        }

        if (fanSpinObservers?.length) {
            for (const obs of fanSpinObservers) {
                try {
                    this.scene.onBeforeRenderObservable.remove(obs);
                } catch {}
            }
            this.metadata.fanSpinObservers = [];
        }

        for (const tex of [...this.scene.textures]) {
            if (tex?.name?.includes("obj")) {
                try {
                    tex.dispose();
                } catch {}
            }
        }

        for (const mat of this.clonedMeshMaterials) {
            try {
                mat.dispose(true, true);
            } catch {}
        }

        try {
            container.removeAllFromScene();
        } catch {}
        try {
            container.dispose(true, true);
        } catch {}

        this.modelRoot = null;
        this.container = null;
        this.clonedMeshMaterials = [];
        this.meshes = [];
        this.metadata.selectedMesh = null;
        this.spriteData.logoYOffset = 0;
    }

    dispose() {
        if (this._disposed) return;
        const safe = (fn) => {
            try {
                fn && fn();
            } catch (_) {}
        };

        safe(() => this._stopRenderLoop?.());
        safe(() => this.engine?.stopRenderLoop?.());

        safe(() => this.disposeModel());

        const scene = this.scene;
        const engine = this.engine;

        if (scene) {
            safe(() => scene.debugLayer?.isVisible() && scene.debugLayer.hide());
            safe(() => scene.stopAllAnimations?.());

            for (const cam of [...(scene.cameras ?? [])]) {
                safe(() => cam.detachControl?.());
                for (const pp of [...(cam.postProcesses ?? [])]) safe(() => pp.dispose?.());
            }

            for (const el of [...(scene.effectLayers ?? [])]) safe(() => el.dispose?.());

            safe(() => this.metadata?.highlighter?.dispose?.());
            if (this.metadata) this.metadata.highlighter = null;

            safe(() => this.metadata?.shadowGenerator?.dispose?.());
            if (this.metadata) this.metadata.shadowGenerator = null;

            for (const l of [...(scene.lights ?? [])]) safe(() => l.dispose?.());
            for (const t of [...(scene.textures ?? [])]) safe(() => t.dispose?.());
            for (const m of [...(scene.materials ?? [])]) safe(() => m.dispose?.(true, true));
            for (const m of [...(scene.meshes ?? [])]) safe(() => m.dispose?.());
            for (const s of [...(this.skyboxes ?? [])]) safe(() => s.dispose?.());
            for (const s of [...(this.groundMeshes ?? [])]) safe(() => s.dispose?.());

            safe(() => scene.onPointerObservable?.clear?.());
            safe(() => scene.onKeyboardObservable?.clear?.());
            safe(() => scene.onBeforeRenderObservable?.clear?.());
            safe(() => scene.onAfterRenderObservable?.clear?.());
            safe(() => scene.onDisposeObservable?.clear?.());

            safe(() => scene.dispose());
        }

        if (engine) {
            safe(() => engine.dispose?.());
        }

        this.camera = null;
        this.modelRoot = null;
        this.container = null;
        this.meshes = [];
        this.clonedMeshMaterials = [];
        this.scene = null;
        this.skyboxes = [];
        this.groundMeshes = [];

        if (this.metadata) {
            this.metadata.directionalLight = null;
        }

        this._disposed = true;
    }
}
